import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormRow } from '@tanium/react-form';
import { Button } from '@tanium/react-button';
import { Box } from '@tanium/react-box';
import { Stack } from '@tanium/react-stack';
import { LoginFormTextInput, LoginFormButton } from './LoginFormComponents';
import { useAsync, AsyncStatus } from '../../hooks/useAsync';
import { ErrorText } from './ErrorText';
import { useOnInputChange } from '../../components/InputHandlerUtils';
import { parseApiErrorMsg, ApiError } from '../../hooks/useApiErrorMsgParser';
import { isValidEmail } from '../../utilities/emailHelper';
import { getDomainSuffix } from '../../utilities/instanceHelper';
import { DomainSuffix } from '../../utilities/constants';

const localAccountExtension = '.mytanium.com';

export interface LoginFormProps {
  onSignIn: (username: string) => Promise<void>;
  onSignInIdp: (email: string) => Promise<void>;
  switchToCreateAccount: () => void;
  switchToForgotUsername: () => void;
}

export const LoginForm: React.VFC<LoginFormProps> = ({
  onSignIn,
  onSignInIdp,
  switchToCreateAccount,
  switchToForgotUsername,
}) => {
  const { t } = useTranslation();

  const [username, setUsername] = useState('');
  const { status, error, execute } = useAsync(() => {
    if (username === '') {
      throw new Error(t('Username is required'));
    }
    if (!isValidEmail(username)) {
      throw new Error(t('Invalid username'));
    }
    if (username.endsWith(localAccountExtension)) {
      return onSignIn(username).catch((err: ApiError) => {
        if (err.response?.status === 404) {
          throw new Error(t('User not found'));
        }
        throw new Error(parseApiErrorMsg(t, err));
      });
    }
    return onSignInIdp(username).catch((err: ApiError) => {
      if (err.response?.status === 404) {
        throw new Error(t('User not found'));
      }
      throw new Error(parseApiErrorMsg(t, err));
    });
  });

  const handleUsernameChange = useOnInputChange(setUsername);

  const handleSubmit = useCallback<React.FormEventHandler>(
    async (event) => {
      event.preventDefault();
      await execute();
    },
    [execute]
  );

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box style={{ flexGrow: 1, display: 'flex', alignItems: 'center', width: '100%' }}>
        <form style={{ width: '100%' }} onSubmit={handleSubmit}>
          <FormRow label={t('Username')} dataKey="username">
            <LoginFormTextInput value={username} onChange={handleUsernameChange} required />
          </FormRow>
          {error && <ErrorText>{error.message}</ErrorText>}
          <Stack itemSpacing="2" justifyContent="left">
            <LoginFormButton loading={status === AsyncStatus.Pending}>{t('Sign In')}</LoginFormButton>
            {getDomainSuffix() !== DomainSuffix.PRD && (
              <Button variant="secondary" onClick={switchToCreateAccount}>
                {t('Create Account')}
              </Button>
            )}
          </Stack>
        </form>
      </Box>
      <Stack justifyContent="center" style={{ margin: 'auto 0 10px 0' }}>
        <Button variant="link" onClick={switchToForgotUsername}>
          {t('Forgot Username')}
        </Button>
      </Stack>
    </Box>
  );
};
